import { useEffect, useMemo, useState } from 'react';
import { StanButton, StanCard } from '@/components';
import { useBalance } from '@/queries/useBalance';
import { useUser } from '@/queries/useUser';
import { useModal } from '@/hooks/useModal';
import { useStanRouter } from '@/hooks/useStanRouter';
import { formatCurrency } from '@/lib/currency';
import { stanAnalytics } from '@/lib/stanAnalytics';
import { fetchPaymentSettings } from '@/lib/api/income';
import IcPlus from '@/assets/icons/ic-plus.svg';
import IcSettings from '@/assets/icons/ic-settings.svg';

import { CashOutTimelineModal } from './CashOutTimelineModal';
import { CashOutModal } from './CashOutModal';

interface CashOutProps {
    className?: string;
    onCashoutSuccess?: () => void;
}

export function CashOut({ className = '', onCashoutSuccess }: CashOutProps) {
    const { balance } = useBalance();
    const { user } = useUser();
    const { push } = useStanRouter();
    const timelineModal = useModal();
    const cashoutModal = useModal();
    const [payoutSchedule, setPayoutSchedule] = useState<number | undefined>(undefined);

    // Pre-fetch payment settings on mount to avoid loading delay in modal
    useEffect(() => {
        const loadPaymentSettings = async () => {
            try {
                const settings = await fetchPaymentSettings();
                setPayoutSchedule(settings.payment_schedule);
            } catch (error) {
                // Silently fail - modal will use fallback text
                console.error('Failed to fetch payment settings:', error);
            }
        };
        loadPaymentSettings();
    }, []);

    const cashoutEnabled = useMemo(() => {
        return balance?.stripe_available && balance.stripe_available?.amount > 0;
    }, [balance]);

    const cashOutAmount = useMemo(() => {
        const stripeAvailableAmount = balance?.stripe_available?.amount || 0;
        const stripeAvailableCurrency = balance?.stripe_available?.currency || 'USD';
        return formatCurrency(stripeAvailableAmount, stripeAvailableCurrency);
    }, [balance?.stripe_available?.amount, balance?.stripe_available?.currency]);

    const availableSoonAmount = useMemo(() => {
        let combinedAmount = 0;
        let currency = 'USD';

        if (balance?.stripe_pending) {
            combinedAmount += balance.stripe_pending.amount;
            currency = balance.stripe_pending.currency;
        }

        if (balance?.affiliate_balance) {
            combinedAmount += balance.affiliate_balance.amount;
        }

        if (balance?.referral_balance) {
            combinedAmount += balance.referral_balance.amount;
        }

        return formatCurrency(combinedAmount, currency);
    }, [balance]);

    const cashoutInTransit = useMemo(() => {
        if (balance?.cashout_in_transit?.amount === 0) {
            return 0;
        }
        return balance?.cashout_in_transit?.amount || 0;
    }, [balance]);

    const cashoutInTransitCurrency = useMemo(() => {
        return balance?.cashout_in_transit?.currency || 'USD';
    }, [balance]);

    // Event handlers
    const handleShowTimelineModal = () => {
        stanAnalytics('open-cash-out-timeline-modal', {
            meta: {
                user_id: user?.user_id,
                username: user?.username,
            },
        });
        timelineModal.open();
    };

    const navigateToSettings = () => {
        push('/account_settings?tab=store');
    };

    const handleCashout = () => {
        cashoutModal.open();
    };

    return (
        <div className={`flex flex-grow flex-col gap-4 ${className}`}>
            <div className='flex flex-col'>
                <StanCard className='relative z-10'>
                    <div className='flex w-full flex-col flex-wrap justify-center gap-6'>
                        <div className='flex w-full flex-col gap-2.5'>
                            <div className='flex flex-row justify-between max-sm:flex-col max-sm:justify-start max-sm:gap-4'>
                                <div>
                                    <div className='text-gray text-caption-2-bold flex items-center'>
                                        Available for Cashout
                                    </div>
                                    <h1 className='font-inter z-10 font-semibold tracking-tight'>{cashOutAmount}</h1>
                                </div>
                                <div className='text-gray flex flex-col gap-2 text-sm'>
                                    <div className='text-caption-2-bold flex flex-col items-end gap-1 max-sm:items-start'>
                                        <div>Available Soon</div>
                                        <h3 className='font-inter z-10 font-semibold tracking-tight'>
                                            {availableSoonAmount}
                                        </h3>
                                    </div>
                                    <div
                                        className='text-caption-2-bold text-brand-main z-10 cursor-pointer self-end transition-colors max-sm:self-start'
                                        onClick={handleShowTimelineModal}
                                    >
                                        View breakdown
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className='flex w-full flex-col items-center justify-center'>
                            <StanButton
                                name='payment-card-cashout-button'
                                icon={IcPlus}
                                className='mb-2 w-full'
                                onClick={handleCashout}
                                disabled={!cashoutEnabled}
                            >
                                Cash Out
                            </StanButton>
                            <StanButton
                                name='payment-card-settings-button'
                                icon={IcSettings}
                                variant='ghost'
                                onClick={navigateToSettings}
                                className='w-full'
                            >
                                Settings
                            </StanButton>
                        </div>
                    </div>
                </StanCard>
            </div>

            {cashoutInTransit > 0 && (
                <div className='animate-in slide-in-from-top-full relative z-0 w-full duration-300'>
                    <div className='bg-brand-soft text-brand-main flex w-full items-center justify-between rounded-lg p-3 text-sm'>
                        <span>Cashout in Progress</span>
                        <span className='font-bold'>{formatCurrency(cashoutInTransit, cashoutInTransitCurrency)}</span>
                    </div>
                </div>
            )}

            <CashOutTimelineModal
                isOpen={timelineModal.isOpen}
                onClose={timelineModal.close}
                availableSoonAmount={availableSoonAmount}
            />

            <CashOutModal
                isOpen={cashoutModal.isOpen}
                onClose={cashoutModal.close}
                payoutSchedule={payoutSchedule}
                onCashoutSuccess={onCashoutSuccess}
            />
        </div>
    );
}
